import {
    Box,
    Button,
    Card,
    Collapse,
    Grid,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    Stack,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
} from '@mui/material'
import React, {useRef, useState, useEffect, useCallback} from 'react'
import {MpTypography} from '../../shared'
import {Controller, FieldValues, UseFieldArrayReturn, UseFormWatch,} from 'react-hook-form'
import deleteIcon from '@meprism/shared/src/assets/icons/Delete.svg'
import editIcon from '@meprism/shared/src/assets/icons/Pencil.svg'
import CountryCode from '@meprism/shared/src/assets/icons/countrycode.svg'
import NumberEmptyIcon from '@meprism/shared/src/assets/icons/number-empty.svg'
import EmailEmptyIcon from '@meprism/shared/src/assets/icons/email-empty.svg'
import {DatePicker} from '@mui/x-date-pickers'
import {ProfileFormHookReturn, ProfileSchemaType, useProfileForm,} from '../../../utils/hooks/useProfileForm'
import MainHeader from '../../shared/organism/MainHeader'
import {theme} from '../../../theme/DashboardTheme'
import {ExpandLess, ExpandMore} from '@mui/icons-material'
import SuccessSubmitModal from '../../shared/atom/SuccessSubmitModal'
import ErrorSubmitModal from '../../shared/atom/ErrorSubmitModal'
import PlusOne from '../../shared/atom/PlusOne'
import { selectActiveGrant,} from '@meprism/shared/src/redux/product/ProductSlice'
import {useAppSelector} from '../../../redux/storeExports'
import {Toast} from '../../toast/toast'
import {UpdateBirthdate, ProfileSelectors} from "@meprism/shared/src/redux/profile/ProfileSlice";
import {useAppDispatch} from "../../../app/hooks";
import PageLayout from '../../layouts/PageLayout'
import {Grant} from '@meprism/shared/src/redux/product/ProductTypes'
import {useLocation} from 'react-router-dom'
import {isAddressIncomplete, formatPhoneNumberDisplay} from '../../../utils/helpers'
import {ReactComponent as CalendarIcon} from '../../../assets/images/calendar.svg'
import {ReactComponent as ShieldIcon} from '../../../assets/images/shield-tick.svg'
import { PhoneNumberMaskedInput } from '../../shared/atom/inputs/PhoneNumberInput'
import ConfirmDobUpdateModal from '../../shared/molecule/ConfirmDobUpdateModal'
import { states } from '@meprism/shared/src/utils/helpers'

interface SidebarProps {
    activeSection: number
    sections: string[]
    onSectionChange: (sectionIndex: number) => void
}

interface PersonalInfoSectionProps {
    firstNameArray: UseFieldArrayReturn<ProfileSchemaType, 'firstName', 'id'>
    middleNameArray: UseFieldArrayReturn<ProfileSchemaType, 'middleName', 'id'>
    lastNameArray: UseFieldArrayReturn<ProfileSchemaType, 'lastName', 'id'>
    otherFirstNameArray: UseFieldArrayReturn<
        ProfileSchemaType,
        'otherFirstName',
        'id'
    >
    otherMaidenNameArray: UseFieldArrayReturn<
        ProfileSchemaType,
        'otherMaidenName',
        'id'
    >
    register: ProfileFormHookReturn['register']
    trigger: ProfileFormHookReturn['trigger']
    errors: ProfileFormHookReturn['formState']['errors']
    control?: ProfileFormHookReturn['control']
    setValue: ProfileFormHookReturn['setValue']
    watch: UseFormWatch<ProfileSchemaType>
}

interface LocationsAddressesSectionProps {
    addressArray: UseFieldArrayReturn<ProfileSchemaType, 'address', 'id'>
    register: ProfileFormHookReturn['register']
    errors: ProfileFormHookReturn['formState']['errors']
    control?: ProfileFormHookReturn['control']
    grant?: Grant
    initialEditableIndex?: number
    addressValues: ProfileSchemaType['address']
    trigger: ProfileFormHookReturn['trigger']
    setValue: ProfileFormHookReturn['setValue']
}

interface ContactInfoSectionProps {
    emailArray: UseFieldArrayReturn<ProfileSchemaType, 'email', 'id'>
    phoneNumberArray: UseFieldArrayReturn<ProfileSchemaType, 'phoneNumber', 'id'>
    phoneNumberArrayValue: { value: string; }[]
    emailArrayValue: { value: string; }[]
    grant?: Grant
    initialEditablePhoneIndex?: number
    register: ProfileFormHookReturn['register']
    errors: ProfileFormHookReturn['formState']['errors']
    control?: ProfileFormHookReturn['control']
}

type AddressType = ProfileSchemaType['address'][number]

type StateCode = (typeof states)[number];

const styles = {
    inputContainer: {
        width: '100%',
        marginLeft: '0px',
    },
    header: {
        color: '#040115',
        mb: 5,
    },
    headingLarge: {
        fontFamily: 'InterMedium',
        fontWeight: 500,
        lineHeight: '27px',
        color: "#3A155A",
        mb: '10px',
    },
    headingMedium: {
        fontFamily: 'InterRegular',
        fontWeight: 400, 
        fontSize: '14px',
        lineHeight: '21px',
        color: '#6B7280',
    },
    fullWidth: {
        width: '100%',
    },
    required: {
        '&:after': {
            content: '"*"',
            color: '#9135E0',
        },
    },
    textField: {
        background: '#EFEDFDB2',
    },
    labelStyles: {
        fontFamily: 'InterRegular',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '140%',
        mb: 1.5,
        color: '#6B7280',
    },
    mb1: {
        marginBlock: '1rem',
    },
    borderBlack: {
        borderColor: '#a5a1a1',
    },
    fieldlabel: {
        mb: 1
    },
    addButton: {
        borderRadius: '10px',
        width: '100%',
        maxWidth: '163px',
        height: '50px',
        backgroundColor: '#9135E0 !important',
        color: '#FFFFFF',
        fontFamily: 'InterRegular',
        fontWeight: 500,
        fontSize: '14px !important',
        lineHeight: '150%',
        letterSpacing: '2%',
        textAlign: 'center',
        '&:hover': {
            backgroundColor: '#9135E0',
        },
    },
    addressTableHeader:{
        fontFamily: 'InterMedium',
        fontWeight: 500,
        fontSize: '14px',
        color: '#3A155A',
        lineHeight: '20px',
        py: 2,
        pl: 2,
    },
    addressTableCell:{
        fontFamily: 'InterRegular',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '20px',
        color: '#6B7280',
        wordBreak: 'break-word', 
        py: 4,
        pl: 2,
    }
} as const


const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
                                                                     register,
                                                                     trigger,
                                                                     errors,
                                                                     control,
                                                                     firstNameArray,
                                                                     lastNameArray,
                                                                     middleNameArray,
                                                                     otherFirstNameArray,
                                                                     otherMaidenNameArray,
                                                                     setValue,
                                                                     watch,
                                                                 }) => {
    const linkedInValue = watch('linkedIn');
    const dispatch = useAppDispatch();
    const profile = useAppSelector(ProfileSelectors.selectProfile);

    // --- DOB Update Logic ---
    const [isDobModalOpen, setIsDobModalOpen] = useState(false);
    const [newDob, setNewDob] = useState<Date | null>(null);
    const [originalDob, setOriginalDob] = useState<Date | null>(null);

    const [dobLocked, setDobLocked] = useState(false);

    const [edit, setEdit] = useState(false)

    const dobCounter = profile?.metadata?.dob_counter ?? 0;
    const isDobDisabled = dobCounter >= 1 || dobLocked;

    const handleDobUpdateAgree = async () => {
        if (newDob) {
            try {
                const formattedDate = newDob.toISOString().split('T')[0];
                
                await dispatch(UpdateBirthdate(formattedDate));
                
                Toast.show({ type: 'success', text1: 'Birthdate updated successfully!' });
                setOriginalDob(newDob);
                setDobLocked(true);
            } catch (error) {
                console.error("Failed to update birthdate:", error);
                Toast.show({ type: 'error', text1: 'Failed to update birthdate.' });
                if(originalDob) setValue('birthdate', originalDob, { shouldDirty: true });
                setDobLocked(false);
            } finally {
                setIsDobModalOpen(false);
                setNewDob(null);
            }
        }
    };

    const handleDobUpdateDisagree = useCallback(() => {
        if (originalDob) setValue('birthdate', originalDob, { shouldDirty: true });
        setIsDobModalOpen(false);
        setNewDob(null);
    }, [originalDob, setValue]);

    const handleDirectDobUpdate = async (newDate: Date) => {
        try {
        const formattedDate = newDate.toISOString().split('T')[0];
        
        await dispatch(UpdateBirthdate(formattedDate));
        
        Toast.show({ type: 'success', text1: 'Birthdate updated successfully!' });
        setOriginalDob(newDate);
        setDobLocked(true);
        } catch (error) {
        console.error("Failed to update birthdate:", error);
        Toast.show({ type: 'error', text1: 'Failed to update birthdate.' });
        if (originalDob) setValue('birthdate', originalDob, { shouldDirty: true });
        setDobLocked(false);
        }
    };

    const isMoreThanOneYearApart = (date1: Date, date2: Date): boolean => {
    const olderDate = date1 < date2 ? date1 : date2;
    const newerDate = date1 < date2 ? date2 : date1;
    
    // Create a date that is exactly 1 year from the older date
    const oneYearLater = new Date(olderDate);
    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
  
    // Check if the newer date is AFTER (strictly greater than) one year later
    return newerDate.getTime() > oneYearLater.getTime();
    };

    const isMobile = useMediaQuery('(max-width:600px)')

    return (
        <>
            <ThemeProvider theme={theme}>
            <Stack 
                direction={{ xs: 'column', md: 'column' }}
                >
            {/* Basic Info Card */}
            <Card
                sx={{
                    py: !isMobile ? '30px' : 0,
                    px: !isMobile ? '16px' : '2.5px',
                    mb: 4,
                    mt: !isMobile ? 5: 0,
                    borderRadius: '15px',
                    boxShadow: isMobile ? 'none'  : '0px 6px 9.2px 0px #0000000A',
                }}
            >
                <Box sx={{width: '100%', display: 'flex', justifyContent: isMobile ? 'flex-end':'space-between', alignItems: 'flex-start'}}>
                {!isMobile && (
                    <Typography
                    sx={{
                        ...styles.headingLarge,
                        fontSize: '20px',
                    }}>
                        Basic Info
                    </Typography>
                )}
                    <img
                        onClick={() => {
                            setEdit(prev => !prev)
                        }}
                        src={editIcon}
                        alt="Description of the image"
                        style={{
                            cursor: 'pointer',
                            maxWidth: '40px',
                            maxHeight: '40px',
                            marginRight: isMobile ? 0 : '10px',
                        }}
                    />
                </Box>
                <Stack width={{md: '92%', lg: '90%'}} spacing={6} display={'flex'} flexDirection={'column'}>
                    <Typography sx={{
                        ...styles.headingMedium,
                        mt: 10,
                    }}>
                        We use your information to look for additional records of yours.
                        Additional information like alternative names helps us find more of
                        your data.
                    </Typography>

                    <Stack display={'flex'} flexDirection={['column', 'row']}>
                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            sx={{width: '100%'}}>
                            {firstNameArray.fields[0] && (
                                <Grid item md={4} sm={12} xs={12} sx={{width: '100%'}}>
                                    <Typography
                                        sx={{
                                            ...styles.required,
                                            ...styles.labelStyles,
                                            }}>
                                        First Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        required
                                        disabled={edit && !firstNameArray.fields[0]?.value ? false : true}
                                        defaultValue={''}
                                        placeholder="Enter your first name"
                                        error={!!errors?.firstName?.[0]?.value}
                                        helperText={errors?.firstName?.[0]?.value?.message}
                                        InputLabelProps={{shrink: true}}
                                        {...register(`firstName.${0}.value` as const)}
                                        sx={{
                                            backgroundColor: '#FFF',
                                            '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            height: '50px',
                                            backgroundColor: '#FFF', 
                                            '& fieldset': {
                                                border: edit && !firstNameArray.fields[0]?.value ? '1.5px solid #9135E0 !important' : '1.5px solid #E2E8F0 !important'
                                            },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#3A155A',
                                                paddingLeft: '20px',
                                                fontFamily: 'InterRegular',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '140%',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                opacity: 1,
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                fontFamily: 'InterRegular',
                                            },
                                            '& .Mui-disabled .MuiInputBase-input::placeholder': {
                                                '-webkit-text-fill-color': '#A0AEC0',
                                            }
                                        }}
                                        onChange={(e) => {
                                            firstNameArray.update(0, {value: e.target.value})
                                        }}
                                    />
                                </Grid>
                            )}
                            {middleNameArray.fields[0] && (
                                <Grid item md={4} sm={12} sx={{width: '100%'}}>
                                    <Typography
                                        sx={{
                                            ...styles.inputContainer,
                                            ...styles.labelStyles,
                                        }}>
                                        Middle Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        disabled={edit && !middleNameArray.fields[0]?.value ? false : true}
                                        defaultValue={middleNameArray.fields[0]?.value || ''}
                                        placeholder="Enter your middle name"
                                        error={!!errors?.middleName?.[0]?.value}
                                        helperText={errors?.middleName?.[0]?.value?.message}
                                        InputLabelProps={{shrink: true}}
                                        {...register(`middleName.${0}.value` as const)}
                                        sx={{
                                            backgroundColor: '#FFF',
                                            '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            height: '50px',
                                            backgroundColor: '#FFF', 
                                            '& fieldset': {
                                                border: edit && !middleNameArray.fields[0]?.value ? '1.5px solid #9135E0 !important' : '1.5px solid #E2E8F0 !important'
                                            },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#3A155A',
                                                paddingLeft: '20px',
                                                fontFamily: 'InterRegular',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '140%',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                opacity: 1,
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                fontFamily: 'InterRegular',
                                            },
                                            '& .Mui-disabled .MuiInputBase-input::placeholder': {
                                                '-webkit-text-fill-color': '#A0AEC0',
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            {lastNameArray.fields[0] && (
                                <Grid item md={4} sm={12} sx={{width: '100%'}}>
                                    <Typography
                                        sx={{
                                            ...styles.required,
                                            ...styles.labelStyles,
                                        }}
                                        >
                                        Last Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        disabled={edit && !lastNameArray.fields[0]?.value ? false : true}
                                        error={!!errors?.lastName?.[0]?.value}
                                        placeholder="Enter your last name"
                                        helperText={errors?.lastName?.[0]?.value?.message}
                                        InputLabelProps={{shrink: true}}
                                        defaultValue={''}
                                        {...register(`lastName.${0}.value` as const)}
                                        sx={{
                                            backgroundColor: '#FFF',
                                            '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            height: '50px',
                                            backgroundColor: '#FFF', 
                                            '& fieldset': {
                                                border: edit && !lastNameArray.fields[0]?.value ? '1.5px solid #9135E0 !important' : '1.5px solid #E2E8F0 !important'
                                            },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#3A155A',
                                                paddingLeft: '20px',
                                                fontFamily: 'InterRegular',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '140%',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                opacity: 1,
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                fontFamily: 'InterRegular',
                                            },
                                            '& .Mui-disabled .MuiInputBase-input::placeholder': {
                                                '-webkit-text-fill-color': '#A0AEC0',
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Stack>
                    <Stack display={'flex'} flexDirection={['column', 'row']}>
                    <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    sx={{width: '100%'}}>
                    
                    {/* Birthdate */}
                    <Grid item md={4} sm={12} sx={{width: '100%'}}>
                        <Typography 
                        sx={{
                            ...styles.required,
                            ...styles.labelStyles,
                        }}>
                        Birthday
                        </Typography>
                        <Controller
                        name={'birthdate'}
                        control={control}
                        
                        rules={{ 
                            validate: (value) => {
                            if (!value) return true;
                            const today = new Date();
                            const birthDate = new Date(value);
                            let age = today.getFullYear() - birthDate.getFullYear();
                            const m = today.getMonth() - birthDate.getMonth();
                            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                                age--;
                            }
                            return age >= 18 || 'You must be at least 18 years old.';
                            }
                        }}
                        render={({field}) => (
                            <DatePicker
                            {...field}
                            inputFormat="MM / DD / YYYY"
                            disableFuture
                            disabled={!edit || isDobDisabled}
                            components={{
                                OpenPickerIcon: CalendarIcon,
                            }}
                            onOpen={() => {setOriginalDob(field.value);}}
                            onAccept={(newValue: any) => {
                                if (newValue && originalDob) {
                                let newDate: Date;
                                let originalDate: Date;
                                
                                try {

                                    // For newDate:
                                    if (newValue instanceof Date) {
                                    newDate = newValue;
                                    } else if (newValue && typeof newValue === 'object' && 'toDate' in newValue && typeof (newValue as any).toDate === 'function') {
                                    newDate = (newValue as any).toDate();
                                    } else {
                                    newDate = new Date(newValue);
                                    }

                                    // For originalDate:
                                    if (originalDob instanceof Date) {
                                    originalDate = originalDob;
                                    } else if (originalDob && typeof originalDob === 'object' && 'toDate' in originalDob && typeof (originalDob as any).toDate === 'function') {
                                    originalDate = (originalDob as any).toDate();
                                    } else {
                                    originalDate = new Date(originalDob);
                                    }

                                    // Validate that both dates are valid
                                    if (isNaN(newDate.getTime()) || isNaN(originalDate.getTime())) {
                                    console.error('Invalid date(s) detected during conversion');
                                    return;
                                    }

                                    if (newDate.getTime() !== originalDate.getTime()) {
                                    // Calculate year difference
                                    const isMoreThan1Year = isMoreThanOneYearApart(newDate, originalDate);
                                    setTimeout(() => {
                                        trigger('birthdate').then(isValid => {
                                        if (isValid) {
                                            // Check if year difference is exactly 1
                                            if (isMoreThan1Year) {
                                            // Show confirmation dialog for +1/-1 year difference
                                            setNewDob(newDate);
                                            setIsDobModalOpen(true);
                                            } else {
                                            // Update directly without confirmation for other year differences
                                            handleDirectDobUpdate(newDate);
                                            }
                                        }
                                        });
                                    }, 0);
                                    }
                                } catch (error) {
                                    console.error('Error comparing dates:', error);
                                }
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                {...params}
                                fullWidth
                                error={!!errors?.birthdate}
                                helperText={errors?.birthdate?.message ?? ''}
                                sx={{
                                    backgroundColor: '#FFF',
                                    '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    height: '50px',
                                    backgroundColor: '#FFF',
                                    '& fieldset': {
                                        border: edit && !isDobDisabled ? '1.5px solid #9135E0 !important' : '1.5px solid #E2E8F0 !important',
                                    },
                                    },
                                    '& .MuiInputBase-input': {
                                    color: '#3A155A',
                                    paddingLeft: '20px',
                                    fontFamily: 'InterRegular',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '140%',
                                    },
                                    '& .MuiInputAdornment-root .MuiIconButton-root': {
                                        padding: 0,
                                        marginRight: 1,
                                    },
                                }}
                                />
                            )}
                            />
                        )}
                        />
                    </Grid>

                    {/* Linkedln */}
                    <Grid
                        item
                        md={4} 
                        sm={12}
                        columnSpacing={isMobile ? 0 : 2}
                        rowSpacing={2}
                        sx={{
                            width: '100%',
                        }}>
                            <Typography 
                                sx={{
                                    ...styles.labelStyles,
                                }}
                            >
                                LinkedIn URL
                            </Typography>
                            <TextField
                                fullWidth
                                // disabled={true}
                                className='basic-info-linkedin-url-textfield'
                                defaultValue={linkedInValue}
                                disabled={!edit}
                                error={!!errors?.linkedIn}
                                helperText={errors?.linkedIn?.message}
                                InputLabelProps={{shrink: true}}
                                {...register('linkedIn', {
                                    onChange: async (e) => {
                                        setValue('linkedIn', e.target.value)
                                        await trigger('linkedIn')
                                    },
                                    onBlur: async () => {
                                        await trigger('linkedIn')
                                    },
                                })}
                                sx={{  
                                    backgroundColor: '#FFF',
                                    '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    height: '50px',
                                    backgroundColor: '#FFF', 
                                    '& fieldset': {
                                        border: edit ? '1.5px solid #9135E0 !important' : '1.5px solid #E2E8F0 !important'
                                    },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#3A155A',
                                        paddingLeft: '20px',
                                        fontFamily: 'InterRegular',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '140%',
                                    },
                                }}
                            />
                    </Grid>
                    </Grid>
                    </Stack>
                </Stack>
            </Card>
            
            {/* Other names card */}
            <Card
                sx={{
                    py: !isMobile ? '30px' : 0,
                    px: !isMobile ? '16px' : '2.5px',
                    mb: 4,
                    height: isMobile ? 'auto' : 435,
                    borderRadius: '15px',
                    boxShadow: isMobile ? 'none'  : '0px 6px 9.2px 0px #0000000A',
                }}
            >
                <Stack width={{md: '92%', lg: '90%'}}>
                       <Stack spacing={2} display={'flex'} flexDirection={'column'}>
                        <Typography 
                        sx={{
                            fontFamily: 'InterMedium',
                            fontWeight: 500,
                            fontSize: '20px',
                            lineHeight: '27px',
                            color:"#3A155A",
                        }}
                        >
                            Other Names
                        </Typography>
                        <Typography 
                        sx={{ 
                            mt: 0,
                            fontFamily: 'InterRegular',
                            fontWeight: 400, 
                            fontSize: '14px',
                            lineHeight: '21px',
                            color: '#6B7280',
                        }}
                        >
                            Optional: You can include alternative names like nicknames, maiden
                            names, or names from marriage changes. For example,
                            &quot;Robert&quot; could be &quot;Bob&quot;. We&apos;ll use
                            whatever full name you provide to conduct our search.
                        </Typography>
                        <Stack
                            display={'flex'}
                            flexDirection={['column', 'row']}>
                            <Grid
                                container
                                spacing={2}
                                marginTop={2}
                                className={'basic-info-other-names'}
                                alignItems="center"
                                sx={{width: '100%'}}>
                                <>
                                <Grid item md={4} sm={12} sx={{width: '100%'}} className={'basic-info-other-names'}>
                                    <Typography
                                        sx={styles.labelStyles}
                                    >
                                        First Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        disabled={edit && !otherFirstNameArray.fields[0]?.value ? false : true }
                                        defaultValue={''}
                                        placeholder='Enter your first name'
                                        InputLabelProps={{shrink: true}}
                                        {...register(`otherFirstName.${0}.value` as const)}
                                        error={!!errors?.otherFirstName?.[0]?.value}
                                        helperText={
                                            errors?.otherFirstName?.[0]?.value?.message || ''
                                        }
                                        sx={{
                                            backgroundColor: '#FFF',
                                            '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            height: '50px',
                                            backgroundColor: '#FFF', 
                                            '& fieldset': {
                                                border: edit && !otherFirstNameArray.fields[0]?.value ? '1.5px solid #9135E0 !important' : '1.5px solid #E2E8F0 !important',
                                            },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#3A155A',
                                                paddingLeft: '20px',
                                                fontFamily: 'InterRegular',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '140%',
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                opacity: 1,
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                fontFamily: 'InterRegular',
                                            },
                                            '& .Mui-disabled .MuiInputBase-input::placeholder': {
                                                '-webkit-text-fill-color': '#A0AEC0',
                                            }
                                        }}
                                        
                                    />
                                </Grid>
                                <Grid item md={4} sm={12} sx={{width: '100%'}}>
                                    <Typography
                                        sx={styles.labelStyles}
                                    >
                                        Maiden Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        defaultValue={''}
                                        placeholder='Enter your maiden name'
                                        disabled={edit && !otherMaidenNameArray.fields[0]?.value ? false : true }
                                        InputLabelProps={{shrink: true}}
                                        {...register(`otherMaidenName.${0}.value` as const)}
                                        error={!!errors?.otherMaidenName?.[0]?.value}
                                        helperText={
                                            errors?.otherMaidenName?.[0]?.value?.message || ''
                                        }
                                        sx={{
                                            backgroundColor: '#FFF',
                                            '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            height: '50px',
                                            backgroundColor: '#FFF', 
                                            '& fieldset': {
                                                border: edit && !otherMaidenNameArray.fields[0]?.value ? '1.5px solid #9135E0 !important' : '1.5px solid #E2E8F0 !important',
                                            },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: '#3A155A',
                                                paddingLeft: '20px',
                                                fontFamily: 'InterRegular',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '140%',
                                            },
                                           '& .MuiInputBase-input::placeholder': {
                                                opacity: 1,
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                fontFamily: 'InterRegular',
                                            },
                                            '& .Mui-disabled .MuiInputBase-input::placeholder': {
                                                '-webkit-text-fill-color': '#A0AEC0',
                                            }
                                        }}
                                    />
                                </Grid>
                                </>
                            </Grid>
                        </Stack>
                    </Stack>
                </Stack>
            </Card>
            </Stack>
            </ThemeProvider>
            <ConfirmDobUpdateModal
                isDobModalOpen={isDobModalOpen}
                handleDobUpdateAgree={handleDobUpdateAgree}
                handleDobUpdateDisagree={handleDobUpdateDisagree}
            />
        </>
    )
}

const LocationsAddressesSection: React.FC<LocationsAddressesSectionProps> = ({
                                                                                 addressArray,
                                                                                 register,
                                                                                 errors,
                                                                                 grant,
                                                                                 control,
                                                                                 setValue,
                                                                                 initialEditableIndex,
                                                                                 addressValues,
                                                                                 trigger
                                                                             }) => {
    const isMobile = useMediaQuery('(max-width:600px)')
    const [editableIndex, setEditableIndex] = useState(initialEditableIndex ?? -1)

    useEffect(() => {
        if (initialEditableIndex) {
            setEditableIndex(initialEditableIndex - 1)
        }
    }, [initialEditableIndex])

    const handleDelete = (array: FieldValues, index: number) => {
        array.remove(index)
    }
    const handleAddAddress = async () => {
        const lastAddress = addressValues[addressArray.fields.length - 1]
        const isIncomplete = isAddressIncomplete(lastAddress, grant)

        if (isIncomplete) {
            Toast.show({
            type: 'warning',
            text1: 'Address left blank. Add a new address or delete the field to trigger a new scan.',
            })
            return
        }
        const isValid = await trigger('address');
        if (!isValid) return;
        
        if (addressArray.fields.length <= 6) {
            addressArray.append({
                state: '' as 'CA',
                city: '',
                streetAddress: '',
                zip: ''
            })
            setEditableIndex(addressArray.fields.length)
        } else {
            Toast.show({
                type: 'warning',
                text1: 'You can not add more than 6 secondary addresses.',
            })
        }
    }

    const isAddressComplete = useCallback((address: AddressType): boolean => {
        return Boolean(
            address.city?.trim() &&
            address.state?.trim() &&
            address.zip?.trim()
        )
    },[])

    return (
        <ThemeProvider theme={theme}>
            <Stack  direction={{ xs: 'column', md: 'column' }}>
                <Card
                    sx={{
                        py: !isMobile ? '30px' : 0,
                        px: !isMobile ? '16px' : '2.5px',
                        mb: 4,
                        mt: !isMobile ? 5 : 0,
                        minHeight: '769px',
                        borderRadius: '15px',
                        boxShadow: isMobile ? 'none'  : '0px 6px 9.2px 0px #0000000A',
                        flexDirection: isMobile ? 'column' : 'row'
                    }}
                >
                    {!isMobile && (
                        <Typography sx={{
                            ...styles.headingLarge,
                            fontSize: '20px',
                        }}>
                            Addresses
                        </Typography>
                    )}
                    <Typography
                    sx={{
                       ...styles.headingMedium,
                       mb:5,
                    }}
                    >
                        We use your information to look for additional records of yours.
                        Additional information such as previous addresses and alternative
                        names helps us find more of your data.
                    </Typography>

                    <Stack>
                        {addressArray.fields.map((field, index) => (
                            <Box key={field.id}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                        mb: 4,
                                    }}>
                                    {index == 0 ? (
                                        <MpTypography 
                                        sx={{
                                            fontFamily: 'InterMedium',
                                            fontWeight: 500, 
                                            fontSize: '16px',
                                            lineHeight: '27px',
                                            color: '#3A155A',
                                        }}
                                        >
                                            Primary Address
                                        </MpTypography>
                                    ) : (
                                        <MpTypography   
                                        sx={{
                                            fontFamily: 'InterMedium',
                                            fontWeight: 500, 
                                            fontSize: '16px',
                                            lineHeight: '27px',
                                            color: '#3A155A',
                                                marginTop: 4,
                                        }}
                                        >
                                            Address {index + 1}
                                        </MpTypography>
                                    )}
                                </Box>
                                <Grid container columnGap={2} rowGap={4} sx={{ width: '100%', margin: 0, }}>

                                    {/* Street Address */}
                                    <Grid item md={5} xs={10} sx={{width: '100%'}}>
                                        <Typography 
                                            sx={{
                                                ...styles.labelStyles,
                                                ...(grant ? styles.required : {})
                                            }}>
                                            Street Address
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            disabled={index !== editableIndex}
                                            error={!!errors?.address?.[index]?.streetAddress}
                                            helperText={
                                                errors?.address?.[index]?.streetAddress?.message || ''
                                            }
                                            InputLabelProps={{shrink: true}}
                                            {...register(`address.${index}.streetAddress` as const)}
                                            onChange={(e) => {
                                                const filteredValue = e.target.value.replace(/^\s+/, '').replace(/\s+/g, ' ');
                                                setValue(`address.${index}.streetAddress`, filteredValue);
                                            }}
                                            sx={{
                                                backgroundColor: '#FFF',
                                                '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                width: '100%',
                                                height: '50px',
                                                backgroundColor: '#FFF', 
                                                '& fieldset': {
                                                    border: index !== editableIndex ? '1.5px solid #E2E8F0 !important' : '1.5px solid #9135E0 !important'
                                                },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: '#3A155A',
                                                    paddingLeft: '20px',
                                                    fontFamily: 'InterRegular',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '140%',
                                                },
                                            }}
                                        />
                                    </Grid>

                                    {/* City */}
                                    <Grid item md={3} xs={10} sx={{width: '100%'}}>
                                        <Typography
                                            sx={Object.assign({}, styles.required, styles.labelStyles)}>
                                            City
                                        </Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            disabled={index !== editableIndex}
                                            sx={{
                                                backgroundColor: '#FFF',
                                                '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                width: '100%',
                                                height: '50px',
                                                backgroundColor: '#FFF', 
                                                '& fieldset': {
                                                    border: index !== editableIndex ? '1.5px solid #E2E8F0 !important' : '1.5px solid #9135E0 !important'
                                                },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: '#3A155A',
                                                    paddingLeft: '20px',
                                                    fontFamily: 'InterRegular',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '140%',
                                                },
                                            }}
                                            error={!!errors?.address?.[index]?.city}
                                            helperText={errors?.address?.[index]?.city?.message || ''}
                                            InputLabelProps={{shrink: true}}
                                            {...register(`address.${index}.city` as const)}
                                            onChange={(e) => {
                                                const filteredValue = e.target.value.replace(/^\s+/, '').replace(/\s+/g, ' ');
                                                setValue(`address.${index}.city`, filteredValue);
                                            }}
                                        />
                                    </Grid>

                                    {/* State */}
                                    <Grid item md={1} xs={10} sx={{width: '100%'}}>
                                        <Typography
                                            sx={Object.assign({}, styles.required, styles.labelStyles)}>
                                            State
                                        </Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            disabled={index !== editableIndex}
                                            sx={{
                                                backgroundColor: '#FFF',
                                                '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                height: '50px',
                                                width: '100%',
                                                backgroundColor: '#FFF', 
                                                '& fieldset': {
                                                    border: index !== editableIndex ? '1.5px solid #E2E8F0 !important' : '1.5px solid #9135E0 !important'
                                                },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: '#3A155A',
                                                    paddingLeft: '20px',
                                                    fontFamily: 'InterRegular',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '140%',
                                                },
                                            }}
                                            error={!!errors?.address?.[index]?.state}
                                            helperText={
                                                errors?.address?.[index]?.state?.message || ''
                                            }
                                            InputLabelProps={{shrink: true}}
                                            {...register(`address.${index}.state` as const, {
                                                setValueAs: (v) => v.toUpperCase(),
                                            })}
                                            onChange={(e) => {
                                                const filteredValue = e.target.value.replace(/\s+/g, '');
                                                setValue(`address.${index}.state`, filteredValue as StateCode);
                                            }}
                                        />
                                    </Grid>

                                    {/* Zip Code */}
                                    <Grid item md={1.5} xs={10} sx={{width: '100%'}}>
                                        <Typography 
                                            sx={Object.assign({}, styles.required, styles.labelStyles)}
                                        >
                                            Zip Code
                                        </Typography>
                                        <Controller
                                        name={`address.${index}.zip`}
                                        control={control}
                                        render={({ field: zipField }) => (
                                        <TextField
                                            {...zipField}
                                            disabled={index !== editableIndex}
                                            fullWidth
                                            sx={{
                                                backgroundColor: '#FFF',
                                                '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                height: '50px',
                                                width: '100%',
                                                backgroundColor: '#FFF', 
                                                overflow: 'hidden',
                                                '& fieldset': {
                                                    border: index !== editableIndex ? '1.5px solid #E2E8F0 !important' : '1.5px solid #9135E0 !important'
                                                },
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: '#3A155A',
                                                    paddingLeft: '20px',
                                                    fontFamily: 'InterRegular',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '140%',
                                                },
                                            }}
                                            error={!!errors?.address?.[index]?.zip}
                                            helperText={errors?.address?.[index]?.zip?.message || ''}
                                            InputLabelProps={{shrink: true}}
                                            onChange={(e) => {
                                                zipField.onChange(e.target.value.replace(/\D/g, '').slice(0, 5))
                                            }}
                                        />
                                        )}
                                        />
                                    </Grid>

                                    {/* edit and delete button */}
                                    <Grid item xs={0.5}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            marginTop: '32px',
                                            marginRight:isAddressComplete(addressArray.fields[index]) ? 0 : {xs: 0, sm: 4, md: 0},
                                        }}
                                    >
                                     {/* Edit Icon */}
                                     <Box 
                                        marginRight={{xs: 1, md: 2}}
                                        mt={1.5}
                                        >
                                        <img
                                            onClick={() => {
                                                if (index === editableIndex) {
                                                    setEditableIndex(-1)
                                                } else {
                                                    setEditableIndex(index)
                                                }
                                            }}
                                            src={editIcon}
                                            alt="Description of the image"
                                            style={{
                                                cursor: 'pointer',
                                                maxWidth: '40px',
                                                maxHeight: '40px',
                                            }}
                                        />
                                    </Box>
                                    
                                    {/* Delete Button */}
                                    {!isAddressComplete(addressArray.fields[index]) && (
                                        <Box sx={{marginTop: 1}}>
                                            <img
                                                src={deleteIcon}
                                                alt="Delete"
                                                style={{ 
                                                    cursor: index === 0 ? 'default' : 'pointer',
                                                    width: 24, 
                                                    height: 24,
                                                    pointerEvents: index === 0 ? 'none' : 'auto',
                                                    opacity: index === 0 ? 0.4 : 1,
                                                    }}
                                                onClick={() => handleDelete(addressArray, index)}
                                            />
                                        </Box>
                                    )}
                                    </Grid>

                                    {/* Add button */}
                                    <Grid md={12} sm={6}>
                                    {index === addressArray.fields.length - 1 && (
                                        <Button
                                            variant={'contained'}
                                            className='address-sidebar-button'
                                            sx={{
                                                ...styles.addButton,
                                                    maxWidth: '163px',
                                            }}
                                            onClick={() => {
                                                handleAddAddress()
                                            }}>
                                            <Box 
                                            component="span"
                                            sx={{
                                                marginRight: { lg: 2, xs: 1 },
                                                marginBottom: 1, 
                                                fontSize: 26, 
                                                fontWeight: 100
                                                }}>
                                                    +
                                            </Box> Add Address
                                        </Button>
                                    )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )
                    )}

                    {/* Address table */}
                    {addressArray.fields.length > 0 && (
                        <Box mt={6} sx={{ maxWidth: isMobile ? 300 : 'auto', overflowX: 'auto', marginBottom: 1.5}}>
                            <Grid 
                                container 
                                columnGap={2}
                                sx={{ 
                                    width: '100%',
                                    pb: 1,
                                    minWidth: '600px',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        width: {xs: '96%', md:'98%', lg: '93.5%'},
                                        borderBottom: '1px solid #E5E5E5'
                                    }
                                }}
                            >
                                <Grid item md={4.91} xs={4}>
                                    <Typography sx={styles.addressTableHeader}>Street Address</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography sx={styles.addressTableHeader}>City</Typography>
                                </Grid>
                                <Grid item xs={1}>
                                    <Typography sx={styles.addressTableHeader}>State</Typography>
                                </Grid>
                                <Grid item xs={1.5}>
                                    <Typography sx={styles.addressTableHeader}>Zip Code</Typography>
                                </Grid>
                                <Grid item md={0.5} xs={1} sx={{padding: 0, margin: 0}}>
                                </Grid>
                            </Grid>

                            {addressArray.fields.filter(isAddressComplete).map((field, index) => (
                                <Grid 
                                    container 
                                    key={index}
                                    columnGap={2}
                                    sx={{
                                        minWidth: '600px',
                                        width: '100%',
                                        position: 'relative',
                                        isolation: 'isolate',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: 0,
                                            zIndex: -1,
                                            left: 0,
                                            top: 0,
                                            height: '100%',
                                            width: {xs: '96%', md:'98%', lg: '93.5%'},
                                            backgroundColor: index % 2 !== 0 ? '#F7F8FA' : '#FFF',
                                            borderBottom: '1px solid #E5E5E5',
                                            borderLeft: '1px solid #E5E5E5',
                                            borderRight: '1px solid #E5E5E5',
                                        }
                                    }}
                                >
                                    <Grid item md={4.91} xs={4} sx={styles.addressTableCell}>
                                        {field.streetAddress}
                                    </Grid>

                                    <Grid item xs={3}
                                        sx={{
                                            ...styles.addressTableCell,
                                            borderLeft: '1px solid #E5E5E5',
                                        }}
                                    >
                                        {field.city}
                                    </Grid>

                                    <Grid item xs={1}
                                        sx={{
                                            ...styles.addressTableCell,
                                            borderLeft: '1px solid #E5E5E5',
                                        }}
                                    >
                                        {field.state}
                                    </Grid>

                                    <Grid item xs={1.5}
                                        sx={{
                                            ...styles.addressTableCell,
                                            borderLeft: '1px solid #E5E5E5',
                                        }}
                                    >
                                        {field.zip}
                                    </Grid>

                                    {index !== 0 && (
                                    <Grid item xs={1.3} md={0.7} lg={0.5}
                                        sx={{
                                            borderLeft: '1px solid #E5E5E5',
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            alignItems: 'center',
                                            py: 4,
                                        }}
                                    >
                                            <img
                                                src={deleteIcon}
                                                alt="Delete"
                                                style={{
                                                    cursor: 'pointer',
                                                    width: 24,
                                                    height: 24,
                                                }}
                                                onClick={() => handleDelete(addressArray, index)}
                                            />
                                    </Grid>
                                    )}
                                </Grid>
                            ))}
                        </Box>
                    )}

                    </Stack>
                </Card>
            </Stack>
        </ThemeProvider>
    )
}
const ContactInfoSection: React.FC<ContactInfoSectionProps> = ({
                                                                   emailArray,
                                                                   phoneNumberArray,
                                                                   phoneNumberArrayValue,
                                                                   emailArrayValue,
                                                                   grant,
                                                                   initialEditablePhoneIndex,
                                                                   register,
                                                                   control,
                                                                   errors,
                                                               }) => {
    const [editableIndex, setEditableIndex] = useState<{ phone: number; email: number }>({
        phone: initialEditablePhoneIndex ?? -1,
        email: -1,
    });


    const handleDelete = (array: FieldValues, index: number) => {
        array.remove(index)
    }
    const addSecondaryNumberOrEmail = (array: FieldValues, type: string) => {
        const lastIndex = array.fields.length - 1;
        const phoneLastIndex = phoneNumberArrayValue.length - 1
        const currentValue =
        type === 'number'
        ? phoneNumberArrayValue[phoneLastIndex].value ?? ''
        : emailArrayValue[lastIndex].value

        if (!currentValue || currentValue.trim() === '' || 
        (type === 'number' && (() => {
            const numericValue = currentValue.replace(/\D/g, '');
            return numericValue.length !== 10;
        })()) ||
        (type === 'email' &&  (currentValue.length > 100 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentValue)))
        ) {
            Toast.show({
                type: 'warning',
                text1:
                type === 'email'
                    ? 'Please enter a valid email before adding another.'
                    : 'Please enter a valid phone number before adding another.',
            });
            return;
        }

        if (array.fields.length < 5) {
            if (array.fields.length === 0) {
                array.insert(array.fields.length, {
                    value: '',
                })
                array.swap(1, 0)
            } else if (array.fields.length === 1) {
                array.insert(array.fields.length, {
                    value: '',
                })
                // array.insert(0, {
                //   value: array.fields[0].value,
                // })
            } else {
                array.insert(array.fields.length, {
                    value: '',
                })
            }
            if (type === 'email') {
                setEditableIndex((prev) => ({ ...prev, email: array.fields.length }))
            }
            else{
                setEditableIndex((prev) => ({ ...prev, phone: array.fields.length }))
            }
        } else {
            array.insert(array.fields.length, {
                value: '',
            })
            array.remove(5)
            setTimeout(() => {
                Toast.show({
                    type: 'warning',
                    text1:
                        type === 'email'
                            ? 'You can not add more than 4 secondary emails.'
                            : 'You can not add more than 4 secondary numbers.',
                })
            }, 500)
        }
    }

    const shouldShowDeleteButton = (
        array: FieldValues,
        index: number
        ): boolean => {
        // Only show delete if it's not the first field and the value is empty
        return index !== 0 && array.fields[index].value === '';
    };

    const isMobile = useMediaQuery('(max-width:600px)')
    return (
        <ThemeProvider theme={theme}>
        <Stack direction={{ xs: 'column', md: 'column' }}>
        {/* Phone number card */}
        <Card
            sx={{
                py: !isMobile ? '30px' : 0,
                px: !isMobile ? '16px' : '2.5px',
                mt: !isMobile ? 5: 0,
                borderRadius: '15px',
                boxShadow: isMobile ? 'none'  : '0px 6px 9.2px 0px #0000000A',
                flexDirection: isMobile ? 'column' : 'row'
            }}
                >
            <Stack width={{md: '98%', lg: '96%'}}>
            {!isMobile && (
                <Typography
                    sx={{
                        ...styles.headingLarge,
                        fontSize: '20px',
                    }}
                    >
                    Phone Number
                </Typography>
            )}

            <Typography
                    sx={{
                        ...styles.headingMedium,
                        mb:5,
                    }}
                    >
                      We use your information to look for additional records of yours.
            </Typography>

            {phoneNumberArray.fields.length ? (
                <Typography sx={{
                    ...styles.labelStyles, 
                    ...(grant && {...styles.required}),
                    }}>
                    Phone Number
                </Typography>
            ) : (
                <Typography variant="h5" mb={1}></Typography>
            )}

            {/* {phoneNumberArray.fields.length === 1 && (
          <Grid container spacing={2} alignItems="center" mt={2}>
            <Grid item md={6} sm={12} sx={{ width: isMobile ? '100%' : 'unset' }}>
              <TextField
                sx={{ width: '100%', background: '#fff' }}
                placeholder="Enter Number"
                disabled={true}
                error={!!errors?.email?.[0]?.value} helperText={errors?.email?.[0]?.value?.message || ''}
                value={phoneNumberArray.fields[0].value}
              />
            </Grid>
          </Grid>
        )}  */}
            {phoneNumberArray.fields.map((field, index) => (
                <Grid container key={field.id} spacing={{xs: 2, lg: 4}} alignItems="center" mb={2}>
                    <Grid display={'flex'} alignItems={'center'} gap={4} item md={11.2} xs={10} sx={{width: isMobile ? '100%' : 'unset'}}>
                        <Controller
                        control={control}
                        name={`phoneNumber.${index}.value`}
                        rules={{
                        validate: {
                            noLetters: (value) => {
                            const cleanValue = value ? value.replace(/\D/g, '') : '';
                            return (
                                !cleanValue || /^[0-9]+$/.test(cleanValue) || 'Only numbers are allowed'
                            );
                            },
                        },
                        }}
                        render={({ field: controllerField, fieldState }) => (
                        <TextField
                            fullWidth
                            disabled={index !== editableIndex.phone}
                             sx={{
                                backgroundColor: '#FFF',
                                '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                height: '50px',
                                width: '100%',
                                backgroundColor: '#FFF', 
                                overflow: 'hidden',
                                '& fieldset': {
                                    border: index !== editableIndex.phone ? '1.5px solid #E2E8F0 !important' : '1.5px solid #9135E0 !important'
                                },
                                },
                                '& .MuiInputBase-input': {
                                    color: '#3A155A',
                                    padding: '8.5px 0px',
                                    fontFamily: 'InterRegular',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '140%',
                                },
                            }}

                            type="tel"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                                inputComponent: PhoneNumberMaskedInput,
                                inputProps: { ...controllerField },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <img
                                            src={CountryCode}
                                            width="20px"
                                            style={{marginRight: 7}}
                                        />
                                        <span style={{fontSize: '14px', color: '#757575'}}>
                                          +1
                                        </span>
                                        <div 
                                            style={{
                                                width: '1px', 
                                                height: '24px', 
                                                background: 'linear-gradient(180deg, rgba(107, 114, 128, 0) 0%, #6B7280 50%, rgba(107, 114, 128, 0) 100%)',          
                                                marginLeft: '8px',
                                                marginRight: '4px'
                                            }} 
                                        />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <img
                                        src={NumberEmptyIcon}
                                        width={20}
                                        height={20}
                                        style={{ marginLeft: 6, cursor: 'pointer' }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        )}
                        />
                        
                    </Grid>

                    {/* edit + delete button */}
                    <Grid item xs={0.8}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            marginTop: {xs: '6px', sm: '4px', md: '5px'},
                            alignItems: 'center',
                            marginBottom: !!errors?.phoneNumber?.[index]?.value ? 5 : 0,
                            marginRight: shouldShowDeleteButton(phoneNumberArray, index) ? {xs: 7, sm: 5, md: 0} : 0,
                        }}
                        >

                        {/* Edit Icon */}
                        <Box 
                            marginRight={{xs: 1, md: 2}}
                            mt={{xs: 1, md:1.5}}
                            mb={{md: '2px'}}
                            >
                            <img
                                onClick={() => {
                                    if (index === editableIndex.phone) {
                                        setEditableIndex(prev => ({...prev, phone: -1}))
                                    } else {
                                        setEditableIndex(prev => ({...prev, phone: index}))
                                    }
                                }}
                                src={editIcon}
                                alt="Description of the image"
                                style={{
                                    cursor: 'pointer',
                                    maxWidth: '40px',
                                    maxHeight: '40px',
                                }}
                            />
                        </Box>
                        {/* Delete Button */}
                        {shouldShowDeleteButton(phoneNumberArray, index) && (
                        <Box>
                            <img
                                src={deleteIcon}
                                alt="Delete"
                                style={{ 
                                    cursor: 'pointer',
                                    width: 22, 
                                    height: 22,
                                    }}
                                onClick={() => handleDelete(phoneNumberArray, index)}
                            />
                        </Box>
                        )}
                    </Grid>

                    <Grid item md={12} sm={6} 
                        sx={{
                            display: 'flex', 
                            justifyContent: 'flex-start', 
                            marginBottom: !!errors?.phoneNumber?.[index]?.value ? 5 : 0,
                        }}
                    >
                        {index === phoneNumberArray.fields.length - 1 && (
                            <Button
                                onClick={() =>
                                    addSecondaryNumberOrEmail(phoneNumberArray, 'number')
                                }
                                sx={{
                                   ...styles.addButton,
                                   minWidth: '145px',
                                }}
                                >
                                <span style={{marginRight: 10, marginBottom:4, fontSize: 26, fontWeight: 100}}>+</span> Add Number
                            </Button>
                        )}
                    </Grid>
                </Grid>
            ))}
            
            {/* Table for phone numbers */}
            {phoneNumberArray.fields.length > 0 && (
            <Box mt={6} sx={{ maxWidth: isMobile ? 310 : 'auto', overflowX: 'auto', marginBottom: 1.5}}>
                {phoneNumberArray.fields.filter((field) => field.value).map((field, index) => (
                    <Grid 
                        container 
                        key={field.id}
                        alignItems="center"
                        sx={{
                            width: {xs: '94%', md: '97%'},  
                            py: 2,
                            position: 'relative',
                            isolation: 'isolate',
                            backgroundColor: index % 2 !== 0 ? '#F7F8FA' : '#FFF',
                            borderBottom: '1px solid #E5E5E5',
                            borderLeft: '1px solid #E5E5E5',
                            borderRight: '1px solid #E5E5E5',
                            borderTop: index === 0 ? '1px solid #E5E5E5' : 'none',
                        }}
                    >
                        <Grid item xs={10} sx={{ 
                            p: 2, 
                            display: 'flex', 
                            alignItems: 'center',
                            fontFamily: 'InterRegular',
                            fontSize: '16px',
                            color: '#6B7280',
                        }}>
                            <img
                                src={CountryCode}
                                alt="US"
                                width="20px"
                                style={{ marginRight: 7 }}
                            />
                            +1 {formatPhoneNumberDisplay(field.value)}
                        </Grid>

                        {index !== 0 && (
                        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 2, }}>
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                <img
                                    src={deleteIcon}
                                    alt="Delete"
                                    style={{ 
                                        cursor: 'pointer',
                                        width: 24, 
                                        height: 24,
                                    }}
                                    onClick={() => handleDelete(phoneNumberArray, index)}
                                />
                            </Box>
                        </Grid>
                        )}
                    </Grid>
                ))}
            </Box>
            )}

            </Stack>
        </Card>
        {/* Email card */}
        <Card
            sx={{
                py: !isMobile ? '30px' : 0,
                px: !isMobile ? '16px' : '2.5px',
                mb: 4,
                mt: 5,
                borderRadius: '15px',
                minHeight: isMobile? 200 : 460,
                boxShadow: isMobile ? 'none'  : '0px 6px 9.2px 0px #0000000A',
                flexDirection: isMobile ? 'column' : 'row'
            }}
            >
            <Stack width={{md: '98%', lg: '96%'}}>
            <Typography
                sx={{
                    ...styles.headingLarge,
                    fontSize: '20px',
                }}
                >
                Email
            </Typography>

            <Typography
                sx={{
                    ...styles.headingMedium,
                    mb:5,
                }}
                >
                We use your information to look for additional records of yours.
            </Typography>

            <Typography
                sx={{
                    ...styles.headingLarge,
                    fontSize: '16px',
                }}
                >
                PRIMARY EMAIL
            </Typography>
            
            {/* Primary email */}
            {emailArray.fields.length ? (
                <Typography sx={{...styles.labelStyles, ...styles.required}}>
                    Primary Email
                </Typography>
            ) : (
                <Typography variant="h5" mb={1}></Typography>
            )}
            <Grid container spacing={{xs: 2, lg: 4}}>
                <Grid item 
                    md={11.2} 
                    xs={10} 
                    sx={{width: isMobile ? '100%' : 'unset'}}
                    >
                    <Box flex={1}>
                        <TextField
                            fullWidth
                            sx={{
                                backgroundColor: '#FFF',
                                '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                height: '50px',
                                width: '100%',
                                paddingLeft: 5,
                                backgroundColor: '#FFF', 
                                overflow: 'hidden',
                                '& fieldset': {
                                    border: emailArray.fields[0]?.value ? '1.5px solid #E2E8F0 !important' : '1.5px solid #9135E0 !important'
                                },
                                },
                                '& .MuiInputBase-input': {
                                    color: '#3A155A',
                                    padding: '8.5px 0px',
                                    fontFamily: 'InterRegular',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '140%',
                                },
                               '& .MuiInputBase-input::placeholder': {
                                    color: '#A0AEC0',
                                    opacity: 1,
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    fontFamily: 'InterRegular',
                                },
                            }}
                            placeholder="Enter Email..."
                            // disabled={index===0 && emailArray.fields.length > 1}
                            disabled={
                                        emailArray.fields[0]?.value !== ''
                            }
                            error={!!errors?.email?.[0]?.value}
                            helperText={errors?.email?.[0]?.value?.message || ''}
                            {...register(`email.${0}.value` as const, {
                                setValueAs: (value) => value.trim(),
                            })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <img
                                        src={EmailEmptyIcon}
                                        width={20}
                                        height={20}
                                        style={{ marginLeft: 6, cursor: 'pointer' }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Grid>
                
                {emailArray.fields.length == 1 && (
                    <Grid item md={11.2} xs={10} sx={{
                        display: 'flex',
                        marginBottom: !!errors?.email?.[0]?.value ? 5 : 0,
                        }}>
                        <Button
                            onClick={() => addSecondaryNumberOrEmail(emailArray, 'email')}
                            sx={{
                                ...styles.addButton,
                                minWidth: '145px',
                            }}>
                           <span style={{marginRight: 10, marginBottom:4, fontSize: 26, fontWeight: 100}}>+</span> Add Email
                        </Button>
                    </Grid> 
                )}
            </Grid>
            {/* {emailArray.fields.length===1 && (
          <Grid container spacing={2} alignItems="center" mt={2}>
            <Grid item md={6} sm={12} sx={{ width: isMobile ? '100%' : 'unset' }}>
              <TextField
                sx={{ width: '100%', background: '#fff' }}
                placeholder="Enter Email..."
                disabled={true}
                error={!!errors?.email?.[0]?.value} helperText={errors?.email?.[0]?.value?.message || ''}
                value={emailArray.fields[0].value}
              />
            </Grid>
          </Grid>
        )} */}
            {emailArray.fields.length === 0 && (
                <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item md={11.2} xs={10} sx={{width: isMobile ? '100%' : 'unset'}}>
                        <TextField
                            sx={{width: '100%', background: '#fff'}}
                            placeholder="Enter Email..."
                            error={!!errors?.email?.[0]?.value}
                            helperText={errors?.email?.[0]?.value?.message || ''}
                            {...register(`email.${0}.value` as const, {
                                setValueAs: (value) => value.trim(),
                            })}
                        />
                    </Grid>
                    <Grid item md={3} sm={12}>
                        {emailArray.fields.length === 0 && (
                            <Button
                                variant={'contained'}
                                className='phone-email-button'
                                color={'darkPurple'}
                                onClick={() => addSecondaryNumberOrEmail(emailArray, 'email')}
                                sx={{
                                    ...styles.addButton,
                                    minWidth: '145px',
                                }}>
                                Add Email
                            </Button>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* other emails */}
            
            {emailArray.fields.length > 1 && (
            <>
            <Typography
                sx={{
                    ...styles.headingLarge,
                    fontSize: '16px',
                    mt:5,
                }}
                >
                OTHER EMAILS
            </Typography>

            <Typography sx={{...styles.labelStyles}}>
                Email
            </Typography>
            {emailArray.fields.map((field, index) => {
                if (index === 0) return null;
                return (
                <Grid container spacing={{xs: 2, lg: 4}} key={field.id} alignItems="center" mb={2}>
                     <Grid display={'flex'} alignItems={'center'} gap={4} item md={11.2} xs={10} sx={{width: isMobile ? '100%' : 'unset'}}>
                        <Box flex={1}>
                        <TextField
                            fullWidth
                             sx={{
                                backgroundColor: '#FFF',
                                '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                height: '50px',
                                width: '100%',
                                paddingLeft: 5,
                                backgroundColor: '#FFF', 
                                overflow: 'hidden',
                                '& fieldset': {
                                    border: index !== editableIndex.email ? '1.5px solid #E2E8F0 !important' : '1.5px solid #9135E0 !important'
                                },
                                },
                                '& .MuiInputBase-input': {
                                    color: '#3A155A',
                                    padding: '8.5px 0px',
                                    fontFamily: 'InterRegular',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '140%',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#A0AEC0',
                                    opacity: 1,
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    fontFamily: 'InterRegular',
                                },
                            }}
                            placeholder="Enter Email..."
                            // disabled={index===0 && emailArray.fields.length > 1}
                            disabled={index !== editableIndex.email}
                            error={!!errors?.email?.[index]?.value}
                            helperText={errors?.email?.[index]?.value?.message || ''}
                            {...register(`email.${index}.value` as const, {
                                setValueAs: (value) => value.trim(),
                            })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <img
                                        src={EmailEmptyIcon}
                                        width={20}
                                        height={20}
                                        style={{ marginLeft: 6, cursor: 'pointer' }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                         </Box>
                    </Grid>

                    {/* edit + delete button */}   
                    <Grid item xs={0.8}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            marginTop: {xs: '6px', sm: '4px', md: '5px'},
                            alignItems: 'center',
                            marginBottom: !!errors?.email?.[index]?.value ? 5 : 0,
                            marginRight: shouldShowDeleteButton(emailArray, index) ? {xs: 7, sm: 5, md: 0} : 0,
                        }}
                        >
                        
                        {/* Edit Icon */}
                        <Box 
                            marginRight={{xs: 1, md: 2}}
                            mt={{xs: 1, md:1.5}}
                            mb={{md: '2px'}}
                            >
                            <img
                                onClick={() => {
                                    if (index == editableIndex.email) {
                                        setEditableIndex(prev => ({...prev, email: -1}))
                                    } else {
                                        setEditableIndex(prev => ({...prev, email: index}))
                                    }
                                }}
                                src={editIcon}
                                alt="Description of the image"
                                style={{
                                    cursor: 'pointer',
                                    maxWidth: '40px',
                                    maxHeight: '40px',
                                }}
                            />
                        </Box>
                        
                        {/* Delete Button */}
                        {shouldShowDeleteButton(emailArray, index) && (
                        <Box>
                            <img
                                src={deleteIcon}
                                alt="Delete"
                                style={{ 
                                    cursor: 'pointer',
                                    width: 22, 
                                    height: 22,
                                    }}
                                onClick={() => handleDelete(emailArray, index)}
                            />
                        </Box>
                        )}
                    </Grid>

                    <Grid item md={12} sm={6}
                        sx={{
                            marginBottom: !!errors?.email?.[index]?.value ? 5 : 0,
                        }}
                    >
                        {index === emailArray.fields.length - 1 && (
                            <Button
                                className='phone-email-button'
                                variant={'contained'}
                                color={'darkPurple'}
                                onClick={() => addSecondaryNumberOrEmail(emailArray, 'email')}
                                sx={{
                                    ...styles.addButton,
                                    minWidth: '145px',
                                }}>
                                <span style={{marginRight: 10, marginBottom:4, fontSize: 26, fontWeight: 100}}>+</span> Add Email
                            </Button>
                        )}
                    </Grid>
                </Grid>
                )})}
            </>
            )}

            {/* Email list table */}
            {emailArray.fields.length > 0 && (
            <Box mt={6} sx={{ maxWidth: isMobile ? 310 : 'auto', overflowX: 'auto', marginBottom: 1.5 }}>
                {emailArray.fields.filter((field) => field.value).map((field, index) => (
                    <Grid 
                        container 
                        key={field.id}
                        alignItems="center"
                        sx={{
                            width: {xs: '94%', md: '97%'},  
                            py: 2,
                            position: 'relative',
                            isolation: 'isolate',
                            backgroundColor: index % 2 !== 0 ? '#F7F8FA' : '#FFF',
                            borderBottom: '1px solid #E5E5E5',
                            borderLeft: '1px solid #E5E5E5',
                            borderRight: '1px solid #E5E5E5',
                            borderTop: index === 0 ? '1px solid #E5E5E5' : 'none',
                        }}
                    >
                        <Grid item xs={10} sx={{ 
                            p: 2, 
                            display: 'flex', 
                            alignItems: 'center',
                            fontFamily: 'InterRegular',
                            fontSize: '16px',
                            color: '#6B7280',
                        }}>
                           {field.value}
                        </Grid>

                        {index !== 0 && (
                        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 2}}>
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                <img
                                    src={deleteIcon}
                                    alt="Delete"
                                    style={{ 
                                        cursor: 'pointer',
                                        width: 24, 
                                        height: 24,
                                    }}
                                    onClick={() => handleDelete(emailArray, index)}
                                />
                            </Box>
                        </Grid>
                        )}
                    </Grid>
                ))}
            </Box>
            )}
        </Stack>
        </Card>
        </Stack>
        </ThemeProvider>
    )
}
const Sidebar: React.FC<SidebarProps> = ({activeSection, sections, onSectionChange}) => {
    const activeGrant = useAppSelector(selectActiveGrant)
    console.log("active section",activeSection)
    return (
        <Box
            sx={{
                display: "flex",
                gap: 3,
                width: '100%',
                height: {sm:'35px', md: '45px'},
            }}
            >
                {sections.map((section, index) =>
                    index === 3 &&
                    activeGrant &&
                    activeGrant.sku &&
                    activeGrant.sku.split('.')[2] === 'fam' ? (
                        <Box
                            key={index}
                            onClick={() => {
                                onSectionChange(index);
                            }}
                            sx={{
                                px: {md: 1, lg:2},
                                cursor: "pointer",
                                color: index === activeSection ? "#9135E0" : "#6B7280",
                                fontSize: {sm: 15, md: 18,lg: 20},
                                fontFamily: index === activeSection ? 'InterMedium' : 'InterRegular',
                                fontWeight: index === activeSection ? 500 : 400,
                                borderBottom: index === activeSection ? '2px solid #9135E0' : 'none',
                            }}
                        >
                            {section}
                        </Box>
                    ) : (
                        index != 3 && (
                        <Box
                            key={index}
                            onClick={() => {
                                onSectionChange(index);
                            }}
                            sx={{
                                px: {md: 1, lg:2},
                                cursor: "pointer",
                                color: index === activeSection ? "#9135E0" : "#6B7280",
                                fontSize: {sm: 15, md: 18,lg: 20},
                                fontFamily: index === activeSection ? 'InterMedium' : 'InterRegular',
                                fontWeight: index === activeSection ? 500 : 400,
                                borderBottom: index === activeSection ? '2px solid #9135E0' : 'none',
                            }}
                        >
                            {section}
                        </Box>
                        )
                    ),
                )}
        </Box>
    )
}

const Profile = () => {
    const location = useLocation()
    const initialSection = location.state?.index || 0
    const fromModal = location.state?.fromModal || false
    const [currentSection, setCurrentSection] = useState(initialSection)
    const [openSections, setOpenSections] = useState<number[]>(initialSection !== 0 ? [initialSection] : [])
    const activeGrant = useAppSelector(selectActiveGrant)
    const [open, setOpen] = useState(false)
    const [openError, setOpenError] = useState(false)
    const [isFormChanged, setIsFormChanged] = useState(false)
    const {
        firstNameArray,
        lastNameArray,
        middleNameArray,
        otherFirstNameArray,
        otherMaidenNameArray,
        addressArray,
        emailArray,
        phoneNumberArray,
        register,
        errors,
        control,
        watch,
        setValue,
        handleSubmit,
        submitHandler,
        trigger,
    } = useProfileForm({
        onSubmitSuccess: () => {
            setOpen(true)
        },
        onSubmitError: () => {
            setOpenError(true)
        },
        middleNameOptional: true,
        streetOptional: !activeGrant,
        linkedinOptional: true,
        phoneNumberOptional: fromModal || !activeGrant,
    })
    const sections = ['Basic Info', 'Address', 'Phone & Email', 'Plus One']
    const handleSectionChangeWeb = (sectionIndex: number) => {
        setCurrentSection(sectionIndex)

        if (!openSections.includes(sectionIndex)) {
            setOpenSections([...openSections, sectionIndex])
        }
    }
    const handleSectionChange = (sectionIndex: number) => {
        setOpenSections((prevOpenSections) => {
            const isOpen = prevOpenSections.includes(sectionIndex)
            
            if (!isOpen) {
                setCurrentSection(sectionIndex)
                return [...prevOpenSections, sectionIndex]
            } else {
                return prevOpenSections.filter((item) => item !== sectionIndex)
            }
        })
    }
    
    const isMobile = useMediaQuery('(max-width:600px)')

    const originalValuesRef = useRef({
        addressArray: '',
        otherFirstNameArray: '',
        otherMaidenNameArray: '',
        emailArray: '',
        middleNameArray: '',
        phoneNumberArray: '',
        linkedIn: '',
        checkChanged: false
    });

    const addressArrayValue = watch('address');
    const otherFirstNameArrayValue = watch('otherFirstName');
    const otherMaidenNameArrayValue = watch('otherMaidenName');
    const middleNameArrayValue = watch('middleName');
    const linkedIn = watch('linkedIn');
    const emailArrayValue = watch('email');
    const phoneNumberArrayValue = watch('phoneNumber');

    useEffect(() => {

        if (originalValuesRef.current.addressArray === "" && addressArray.fields.length >= 1) {
            originalValuesRef.current.addressArray = JSON.stringify(addressArray.fields.map(({
                                                                                                 id: _,
                                                                                                 ...rest
                                                                                             }) => rest));

            if (originalValuesRef.current.otherFirstNameArray === "" && otherFirstNameArray) {
                originalValuesRef.current.otherFirstNameArray = JSON.stringify(otherFirstNameArray.fields.map(({
                                                                                                                   id: _,
                                                                                                                   ...rest
                                                                                                               }) => rest));
            }

            if (originalValuesRef.current.otherMaidenNameArray === "" && otherMaidenNameArray) {
                originalValuesRef.current.otherMaidenNameArray = JSON.stringify(otherMaidenNameArray.fields.map(({
                                                                                                                     id: _,
                                                                                                                     ...rest
                                                                                                                 }) => rest));
            }
            if (originalValuesRef.current.middleNameArray === "" && middleNameArray) {
                originalValuesRef.current.middleNameArray = JSON.stringify(middleNameArray.fields.map(({
                    id: _,
                    ...rest
                }) => rest));
            }

            if (originalValuesRef.current.emailArray === "" && emailArray) {
                originalValuesRef.current.emailArray = JSON.stringify(emailArray.fields.map(({
                                                                                                 id: _,
                                                                                                 ...rest
                                                                                             }) => rest));
            }

            if (originalValuesRef.current.phoneNumberArray === "" && phoneNumberArray) {
                if (phoneNumberArray.fields.length === 0) {
                    originalValuesRef.current.phoneNumberArray = JSON.stringify([{"value": ""}]);
                } else {
                    originalValuesRef.current.phoneNumberArray = JSON.stringify(phoneNumberArray.fields.map(({
                                                                                                                 id: _,
                                                                                                                 ...rest
                                                                                                             }) => rest));
                }
            }

            if (originalValuesRef.current.linkedIn === "") {
                originalValuesRef.current.linkedIn = linkedIn || "";
            }

            originalValuesRef.current.checkChanged = true;
        }

   

         const hasNumberChanged = () =>{
            if(!phoneNumberArrayValue || phoneNumberArrayValue.length === 0){
                return false;
            }
            else{
                return JSON.stringify(phoneNumberArrayValue) !== originalValuesRef.current.phoneNumberArray && 
                phoneNumberArrayValue.every((value)=> value.value != "");
            }
            }
        const hasFormChanged = () => {
            if (JSON.stringify(addressArrayValue) !== originalValuesRef.current.addressArray && addressArrayValue.every((value) => value.city != "")) {
                return true
            }
            if (JSON.stringify(otherFirstNameArrayValue) !== originalValuesRef.current.otherFirstNameArray ||
                JSON.stringify(otherMaidenNameArrayValue) !== originalValuesRef.current.otherMaidenNameArray ||
                JSON.stringify(middleNameArrayValue) !== originalValuesRef.current.middleNameArray) {
                return true
            }
            if (JSON.stringify(emailArrayValue) !== originalValuesRef.current.emailArray && emailArrayValue.every((value) => value.value != "")) {
                return true
            }
            if ((linkedIn) !== originalValuesRef.current.linkedIn) {
                return true;
            }
            if (hasNumberChanged()) {
                return true
            }
            return false
        }

        if (originalValuesRef.current.checkChanged) {
            setIsFormChanged(hasFormChanged());
        }


    }, [addressArray, addressArrayValue, emailArray, emailArrayValue, otherFirstNameArray,middleNameArray, middleNameArrayValue,
        otherFirstNameArrayValue, otherMaidenNameArray, otherMaidenNameArrayValue, phoneNumberArray, phoneNumberArrayValue, linkedIn]);

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!isFormChanged) {
            // Show a message when no changes are detected
            Toast.show({
              type: 'warning',
              text1: 'Add or update your information to run a new scan',
          })
            return
        }

        // If the form has changes, proceed with normal submission
       handleSubmit(submitHandler)(e)
    }

    return (
        <>
            {isMobile ? (
                <PageLayout>
                <Stack
                    mt={isMobile? 18: 'auto'}
                    spacing={5}
                    component={'form'}
                    onSubmit={handleFormSubmit}
                    flexDirection={'column'}
                    marginTop={10}
                    >
                    <MainHeader title={'Profile'}/>
                    <Card sx={{px: 7, py: 5, position: 'relative', borderRadius: 5, boxShadow: 'none'}}>
                        {sections.map((section, index) => (
                            <React.Fragment key={index}>
                                <ListItem disablePadding>
                                    {index === 3 &&
                                    activeGrant &&
                                    activeGrant.sku &&
                                    activeGrant.sku.split('.')[2] === 'fam' ? (
                                        <ListItemButton
                                            sx={{
                                                display: 'flex',
                                                paddingX: 0,
                                                justifyContent: 'space-between',
                                            }}
                                            onClick={() => handleSectionChange(index)}>
                                            <Typography variant="body1" sx={{color: '#1A1B20'}}>
                                                {section}
                                            </Typography>
                                            {openSections.includes(index) ? (
                                                <ExpandLess/>
                                            ) : (
                                                <ExpandMore/>
                                            )}
                                        </ListItemButton>
                                    ) : (
                                        index != 3 && (
                                            <ListItemButton
                                                sx={{
                                                    paddingX: 0,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}
                                                onClick={() => handleSectionChange(index)}>
                                                <Typography variant="body1" sx={{color: '#1A1B20'}}>
                                                    {section}
                                                </Typography>
                                                {openSections.includes(index) ? (
                                                    <ExpandLess/>
                                                ) : (
                                                    <ExpandMore/>
                                                )}
                                            </ListItemButton>
                                        )
                                    )}
                                </ListItem>
                                <Collapse
                                    in={openSections.includes(index)}
                                    timeout="auto"
                                    unmountOnExit>
                                    <List component="div" disablePadding>
                                        <ListItem
                                            disablePadding
                                            sx={{
                                                width: '100%',
                                            }}>
                                            {index === 0 && (
                                                <PersonalInfoSection
                                                    watch={watch}
                                                    setValue={setValue}
                                                    firstNameArray={firstNameArray}
                                                    middleNameArray={middleNameArray}
                                                    lastNameArray={lastNameArray}
                                                    otherFirstNameArray={otherFirstNameArray}
                                                    otherMaidenNameArray={otherMaidenNameArray}
                                                    register={register}
                                                    errors={errors}
                                                    control={control}
                                                    trigger={trigger}
                                                />
                                            )}
                                            {index === 1 && (
                                                <LocationsAddressesSection
                                                    addressArray={addressArray}
                                                    register={register}
                                                    errors={errors}
                                                    control={control}
                                                    grant={activeGrant}
                                                    addressValues={addressArrayValue}
                                                    trigger={trigger}
                                                    setValue={setValue}
                                                />
                                            )}
                                            {index === 2 && (
                                                <ContactInfoSection
                                                    emailArray={emailArray}
                                                    phoneNumberArray={phoneNumberArray}
                                                    phoneNumberArrayValue={phoneNumberArrayValue || []}
                                                    emailArrayValue={emailArrayValue}
                                                    grant={activeGrant}
                                                    register={register}
                                                    errors={errors}
                                                    control={control}
                                                />
                                            )}
                                            {index === 3 &&
                                                activeGrant &&
                                                activeGrant.sku &&
                                                activeGrant?.sku.split('.')[2] === 'fam' && <PlusOne/>}
                                        </ListItem>
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        ))}
                        <Typography variant="body2" sx={{mt: 5, pb: 3}}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <ShieldIcon style={{ width: 24, height: 24, marginRight: 8 }} />
                                <Box>
                                     <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                        color: '#6B7280',
                                        fontWeight: 400,
                                        fontFamily: 'InterRegular',
                                        fontSize: '12px',
                                        letterSpacing: '1px'
                                        }}
                                    >
                                        mePrism uses industry-standard encryption to protect
                                        confidentiality of all information you provide.
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                        color: '#9135E0 !important',
                                        fontFamily: 'InterMedium',
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        ml: 1,
                                        letterSpacing: '1px'
                                        }}
                                    >
                                        We do not sell your data.
                                    </Typography>
                                </Box>
                            </Box>
                        </Typography>
                        <SuccessSubmitModal open={open} setOpen={setOpen}/>
                        <ErrorSubmitModal open={openError} setOpen={setOpenError}/>
                        <Box sx={{...styles.inputContainer, display: 'flex', justifyContent: 'center' }} >
                        <Button
                            className='profile-card-button'
                            type={'submit'}
                            sx={{
                                width: '100%',
                                maxWidth: '291px',
                                height: { xs:'40px', sm: '50px', md: '60px'},
                                paddingTop: '16px',
                                paddingRight: '50px',
                                paddingBottom: '16px',
                                paddingLeft: '50px',
                                backgroundColor: '#9135E0 !important',
                                borderRadius: '10px',
                                '&:hover': {
                                        backgroundColor: '#9135E0 !important',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.7,
                                    },
                            }}
                            >
                            Save & Run New Scan
                        </Button>
                        </Box>
                    </Card>
                </Stack>
                </PageLayout>
            ) : (
                <PageLayout>
                    <Stack
                        component={'form'}
                        onSubmit={handleFormSubmit}>
                        <MainHeader title={'Profile'}/>
                        <Grid container sx={{paddingTop: 3,}}>
                            <Grid container item md={12} xs={12}
                            sx={{
                                display:'flex',
                                alignItems: 'flex-end'
                            }}
                            >
                                <Grid item sm={12} md={8}>
                                    <Sidebar
                                        activeSection={currentSection}
                                        sections={sections}
                                        onSectionChange={handleSectionChangeWeb}
                                    />
                                </Grid>
                                 <Grid item sm={12} md={4} mt={{sm:3, md: 0}} sx={{...styles.inputContainer, display: 'flex', justifyContent: 'flex-end'}}>
                                    <Button
                                        className='profile-card-button'
                                        type={'submit'}
                                        sx={{
                                            width: '100%',
                                            maxWidth: {sm: '230px', md: '291px'},
                                            height: {sm: '45px', md: '60px'},
                                            fontSize: {sm: '14px', lg: '18px'},
                                            fontWeight: 500,
                                            fontFamily: 'InterMedium',
                                            padding: 0,
                                            backgroundColor: '#9135E0 !important',
                                            '&:hover': {
                                                backgroundColor: '#9135E0 !important',
                                            },
                                        }}>
                                        Save & Run New Scan
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid item md={12} xs={12}>
                                {' '}
                                    <Box sx={{position: 'relative', minHeight: '90%'}}>
                                        {currentSection === 0 && (
                                            <PersonalInfoSection
                                                watch={watch}
                                                setValue={setValue}
                                                firstNameArray={firstNameArray}
                                                middleNameArray={middleNameArray}
                                                lastNameArray={lastNameArray}
                                                otherFirstNameArray={otherFirstNameArray}
                                                otherMaidenNameArray={otherMaidenNameArray}
                                                register={register}
                                                errors={errors}
                                                control={control}
                                                trigger={trigger}
                                            />
                                        )}
                                        {currentSection === 1 && (
                                            <LocationsAddressesSection
                                                addressArray={addressArray}
                                                register={register}
                                                errors={errors}
                                                control={control}
                                                grant={activeGrant}
                                                initialEditableIndex={location.state?.fromModal ? addressArray.fields.length : undefined}
                                                addressValues={addressArrayValue}
                                                trigger={trigger}
                                                setValue={setValue}
                                            />
                                        )}

                                        {currentSection === 2 && (
                                            <ContactInfoSection
                                                emailArray={emailArray}
                                                phoneNumberArray={phoneNumberArray}
                                                phoneNumberArrayValue={phoneNumberArrayValue || []}
                                                emailArrayValue={emailArrayValue}
                                                grant={activeGrant}
                                                initialEditablePhoneIndex={location.state?.fromModal ? phoneNumberArray.fields.length : undefined}
                                                register={register}
                                                errors={errors}
                                                control={control}
                                            />
                                        )}
                                        {currentSection === 3 &&
                                            activeGrant &&
                                            activeGrant.sku &&
                                            activeGrant?.sku.split('.')[2] === 'fam' && <PlusOne/>}
                                        <Typography variant="body2" sx={{mt: 5, pb: 3}}>
                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                <ShieldIcon style={{ width: 24, height: 24, marginRight: 8 }} />
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        component="span"
                                                        sx={{
                                                        color: '#6B7280',
                                                        fontWeight: 400,
                                                        fontFamily: 'InterRegular',
                                                        fontSize: '12px',
                                                        letterSpacing: '1px'
                                                        }}
                                                    >
                                                        mePrism uses industry-standard encryption to protect
                                                        confidentiality of all information you provide.
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        component="span"
                                                        sx={{
                                                        color: '#9135E0 !important',
                                                        fontFamily: 'InterMedium',
                                                        fontWeight: 600,
                                                        fontSize: '12px',
                                                        ml: 1,
                                                        letterSpacing: '1px'
                                                        }}
                                                    >
                                                        We do not sell your data.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                    </Typography>
                                    </Box>
                            </Grid>
                        </Grid>
                        <ErrorSubmitModal open={openError} setOpen={setOpenError}/>
                        <SuccessSubmitModal open={open} setOpen={setOpen}/>
                       
                    </Stack>
                </PageLayout>
            )}
        </>
    )
}

export default Profile
